import { countsOnRegularVisit, quoteFirstCleanPrice, roomOnRegularVisit } from '@shared/first-clean-price.ts';
import { dustStatus } from '@shared/dust-evidence.ts';
import { walkthroughStatus } from '@shared/walkthrough-gate.ts';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { quoteApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import {
  clearDraft,
  draftIsDirty,
  loadDraft,
  saveDraft,
  type DraftAcknowledgment,
  type DraftSaveOptions,
  type DraftSaveResult,
  type DraftToSave,
  type QuoteDraft,
} from '../state/draft-store.ts';
import { useDebouncedCallback, useOnlineStatus } from './index.ts';
import { quoteDraftAuthorizationFor, useSession } from '../state/session.tsx';
import type { Quote } from '@shared/types/index.ts';
import { buildChanges, graftLocalEdits, mergeServerIds } from './quote-merge.ts';
import {
  calculateQuote,
  type QuoteCalculationInput,
  type QuoteCalculationResult,
} from '@shared/calculations/pricing.ts';
import { clampGroupQuantity } from '@shared/quotes/groups.ts';
import { conditionMultiplierFor, sizeMultiplierFor } from '@shared/constants/index.ts';
import { areaPricingCalculation, HOURLY_AREA_PRICING, withoutAreaRoomPrices } from '@shared/area-pricing.ts';

/**
 * Quote editor state (spec §11, §17).
 *
 * The editor owns a local working copy. Every change updates it immediately —
 * so the on-screen totals recalculate with no round trip — and an autosave
 * fires two seconds after the last edit, on step changes, and before leaving.
 * The Worker's response is authoritative for pricing and version; the local
 * copy only ever wins for fields the user is still typing into.
 */

export const AUTOSAVE_DELAY_MS = 2000;

/** How long an online clean snapshot waits for a live GET before showing anyway. */
export const CLEAN_SNAPSHOT_LIVE_READ_GRACE_MS = 3_000;

const MAX_EDITOR_ACTIVATIONS = 100;
const editorOwnerSource = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
let nextEditorActivationGeneration = 0;

/**
 * Monotonic activation registry shared by every hook instance in this tab.
 * Reopening A after unmounting A (or after visiting B) must never look like the
 * same editor activation to an acknowledgment captured by the old instance.
 */
const editorActivations = new Map<string, number>();

function activateEditorScope(scope: string): number {
  nextEditorActivationGeneration += 1;
  editorActivations.delete(scope);
  editorActivations.set(scope, nextEditorActivationGeneration);
  while (editorActivations.size > MAX_EDITOR_ACTIVATIONS) {
    const oldest = editorActivations.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    editorActivations.delete(oldest);
  }
  return nextEditorActivationGeneration;
}

function currentEditorActivationGeneration(scope: string): number | null {
  return editorActivations.get(scope) ?? null;
}

export type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'offline' | 'error' | 'conflict';

type RestoreState = 'none' | 'waiting_for_server' | 'ready_to_sync' | 'conflict';

export function draftSaveFailureMessage(result: DraftSaveResult): string | null {
  if (result === 'entry-too-large') {
    return 'This quote is too large for protected offline storage. Reconnect and save it before leaving this page.';
  }
  if (result === 'capacity-reached') {
    return 'Offline quote storage is full. Reconnect and save another draft before continuing offline.';
  }
  if (result === 'inactive') {
    return 'This browser could not protect this quote on this device. Reconnect and save before leaving this page.';
  }
  return null;
}

export interface QuoteEditorApi {
  quote: Quote | null;
  serverQuote: Quote | null;
  isLoading: boolean;
  loadError: Error | null;
  saveState: SaveState;
  saveError: string | null;
  lastSavedAt: Date | null;
  isDirty: boolean;
  isOnline: boolean;
  /** Live preview computed locally, ahead of the server's authoritative figures. */
  preview: QuoteCalculationResult;
  update: (mutate: (draft: Quote) => Quote) => void;
  save: () => Promise<Quote | null>;
  reloadFromServer: () => Promise<void>;
  applyServerQuote: (quote: Quote, savedThrough?: number) => void;
  /** Edit count the server has seen, for `applyServerQuote`'s `savedThrough`. */
  savedEditCount: () => number;
  /**
   * Holds the debounced autosave while a version-bumping action (publish,
   * send, PDF) is in flight, so keystrokes made during it cannot fire a save
   * carrying the pre-action version — which the server would refuse as a
   * conflict nobody actually caused. Returns the resume function; resuming
   * re-arms the autosave if edits are waiting.
   */
  suspendAutosave: () => () => void;
}

/** What the totals panel shows before a draft has loaded. */
const EMPTY_PREVIEW: QuoteCalculationResult = {
  rooms: [],
  billingBasis: 'per_visit',
  routineVisitMinutes: 0,
  estimatedMinutes: 0,
  billableMinutes: 0,
  laborSubtotalCents: 0,
  fixedFeesCents: 0,
  roomChargesCents: 0,
  discountCents: 0,
  frequencyDiscountCents: 0,
  taxableSubtotalCents: 0,
  taxCents: 0,
  totalCents: 0,
};

/**
 * The pricing engine's inputs, read off a draft in the browser.
 *
 * This is the live preview's half of a pair: the Worker builds the same object
 * from database rows in worker/services/pricing.ts, and the two have to agree field
 * for field or the totals panel shows the estimator one number while the saved
 * quote carries another. A field added to `ServiceCalculationInput` and wired
 * up on only one side is invisible until someone compares a screen to a PDF —
 * which is exactly how per-task tax first shipped here, previewing an exempt
 * task as taxable.
 *
 * Exported so that agreement can be tested rather than hoped for.
 */
export function calculationInputFor(
  source: Quote,
  scope: 'regular' | 'first_visit' = 'regular',
): QuoteCalculationInput {
  const groups = source.groups ?? [];
  // While the quote has a first clean price, its first-visit-only tasks are
  // priced on the first visit and left out of the regular one, as the Worker does.
  const firstCleanOn = source.billingBasis === 'per_visit' && quoteFirstCleanPrice(source) !== null;
  const quantityOf = (groupId: string | null | undefined) => {
    const group =
      groups.find((candidate) => candidate.id === groupId) ?? groups.find((candidate) => candidate.isDefault);
    return clampGroupQuantity(group?.quantity);
  };
  return {
    ...areaPricingCalculation(
      source.areaPricing ?? HOURLY_AREA_PRICING,
      source.billingBasis === 'monthly',
      source.currencyCode,
    ),
    dustUpliftBasisPoints: source.dust ? dustStatus(source.dust, source.dust.evidenceVerified).upliftBasisPoints : 0,
    pricingBlocked:
      (source.dust ? dustStatus(source.dust, source.dust.evidenceVerified).blocked : false) ||
      source.pricingBlocked ||
      (source.walkthrough
        ? walkthroughStatus(source.walkthrough, {
            id: source.id,
            companyId: source.walkthrough.review?.companyId ?? '',
            locationId: source.locationId,
            propertyId: source.propertyId,
          }).pricingBlocked
        : false) ||
      areaPricingCalculation(
        source.areaPricing ?? HOURLY_AREA_PRICING,
        source.billingBasis === 'monthly',
        source.currencyCode,
      ).pricingBlocked,
    rooms: source.rooms.map((room) => {
      const regular = scope === 'first_visit' || roomOnRegularVisit(firstCleanOn, room.services);
      return {
        quantity: room.quantity,
        groupQuantity: groups.length === 0 ? 1 : quantityOf(room.groupId),
        sizeMultiplierBasisPoints: sizeMultiplierFor(room.sizeCode),
        conditionMultiplierBasisPoints: conditionMultiplierFor(room.conditionCode),
        adjustedMinutes: regular ? room.adjustedMinutes : null,
        flatPriceCents: regular ? room.flatPriceCents : null,
        measuredSquareFeet: room.measuredSquareFeet,
        services: room.services.map((service) => ({
          estimatedMinutes: service.estimatedMinutes,
          isSelected:
            service.isSelected &&
            (scope === 'first_visit' || countsOnRegularVisit(firstCleanOn, service.firstVisitOnly)),
          isTaxable: service.isTaxable,
          frequency: service.frequency,
          measurementBasis: service.measurementBasis,
          measuredQuantity: service.measuredQuantity,
          productionRatePerHour: service.productionRatePerHour,
        })),
      };
    }),
    fees: source.fees.map((fee) => ({
      amountCents: fee.amountCents,
      isTaxable: fee.isTaxable,
    })),
    hourlyRateCents: source.totals.hourlyRateCents,
    billingIncrementMinutes: source.totals.billingIncrementMinutes,
    billingBasis: source.billingBasis,
    visitsPerWeek: source.visitsPerWeek,
    discountType: source.totals.discountType,
    discountValue: source.totals.discountValue,
    taxEnabled: source.totals.taxRateBasisPoints > 0,
    taxRateBasisPoints: source.totals.taxRateBasisPoints,
    laborIsTaxable: source.totals.laborIsTaxable,
    // The quote's own snapshot: the preview has to agree with what the Worker
    // will write, and the Worker prices in this same currency.
    currencyCode: source.currencyCode,
  };
}

export function useQuoteEditor(quoteId: string): QuoteEditorApi {
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const { user, company, currentLocation, impersonatedBy } = useSession();
  const quoteAuthorization = user && company ? quoteDraftAuthorizationFor(user, company) : null;
  const quoteViewScope = JSON.stringify([
    quoteId,
    user?.id ?? null,
    company?.id ?? null,
    currentLocation?.id ?? null,
    quoteAuthorization,
    impersonatedBy,
  ]);
  const quoteStorageScope =
    user && company && !impersonatedBy
      ? JSON.stringify([user.id, company.id, currentLocation?.id ?? null, quoteAuthorization])
      : null;
  const mayReadQuote = Boolean(user?.permissions.includes('quotes:read') && company && !company.accessLocked);
  const quoteQueryKey = useMemo(() => ['quote', quoteId, quoteViewScope] as const, [quoteId, quoteViewScope]);
  const mayWriteQuote = Boolean(mayReadQuote && user?.permissions.includes('quotes:write'));

  const [draft, setDraft] = useState<Quote | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [storedDraft, setStoredDraft] = useState<QuoteDraft | null>(null);
  const storedDraftRef = useRef<QuoteDraft | null>(null);
  storedDraftRef.current = storedDraft;
  const [liveReadGraceElapsed, setLiveReadGraceElapsed] = useState(false);
  const [draftLookupComplete, setDraftLookupComplete] = useState(false);
  /** IndexedDB lookup finished for this view scope; cleared synchronously on scope change. */
  const draftLookupReadyScopeRef = useRef<string | null>(null);
  const [restoreState, setRestoreState] = useState<RestoreState>('none');
  const [editorStateScope, setEditorStateScope] = useState(quoteViewScope);

  // Counts local edits so a save can tell whether the user typed while it was
  // in flight.
  const editCounter = useRef(0);
  const savedCounter = useRef(0);
  const draftRef = useRef<Quote | null>(null);
  const inFlight = useRef<Promise<Quote | null> | null>(null);
  const resaveWhenDone = useRef(false);
  /** Callers of `save()` that are waiting for a queued follow-up to land. */
  const queuedWaiters = useRef<Array<(quote: Quote | null) => void>>([]);
  /** Lets the in-flight save re-enter `save` without a circular dependency. */
  const saveRef = useRef<(() => Promise<Quote | null>) | null>(null);
  const onlineRef = useRef(isOnline);
  /** Orders async IndexedDB answers within each access scope independently. */
  const localPersistenceAttempts = useRef(new Map<string, number>());
  const localPersistenceFailed = useRef(false);
  const localPersistenceFailureMessage = useRef<string | null>(null);
  const inheritedDraftAcknowledgment = useRef<{
    identity: DraftAcknowledgment | null;
    legacy: Pick<DraftToSave, 'baseVersion' | 'quote'> | null;
  } | null>(null);
  const activeEditorScope = useRef(quoteViewScope);
  const editorActivation = useRef<{ scope: string; generation: number } | null>(null);
  if (!editorActivation.current || editorActivation.current.scope !== quoteViewScope) {
    activeEditorScope.current = quoteViewScope;
    editorActivation.current = {
      scope: quoteViewScope,
      generation: activateEditorScope(quoteViewScope),
    };
  }
  const renderActivationGeneration = editorActivation.current!.generation;
  const editOwnerId = editorOwnerSource + ':' + renderActivationGeneration;
  onlineRef.current = isOnline;

  const query = useQuery({
    queryKey: quoteQueryKey,
    queryFn: ({ signal }) => quoteApi.get(quoteId, signal),
    staleTime: 30_000,
    retry: (failureCount, error) => !(error instanceof ApiRequestError && error.status < 500) && failureCount < 2,
  });

  // A cache is a fallback for network failure, never an override of a live
  // authorization/not-found answer. Dirty work remains recoverable below;
  // only an acknowledged snapshot is hidden for these definitive responses.
  const definitiveLoadError = query.error instanceof ApiRequestError;
  // While a live read is in flight, a cached quote is the previous answer.
  // Adopting it would paint Scheduled from a series count the server is in
  // the middle of recounting, then save that count back as the clean snapshot.
  const serverQuote = definitiveLoadError ? null : (query.data ?? null);
  const adoptableServerQuote = definitiveLoadError || (isOnline && query.isFetching) ? null : (query.data ?? null);
  const isDirty = editCounter.current !== savedCounter.current;
  const hasDirtyRecovery = isDirty || Boolean(storedDraft && draftIsDirty(storedDraft));
  // Effects run after render. Derive the visible value as well as clearing the
  // durable clean snapshot below, otherwise a page that prioritizes `quote`
  // can paint customer data for one frame after a definitive HTTP response.
  const visibleDraft =
    editorStateScope !== quoteViewScope || !mayReadQuote || (definitiveLoadError && !hasDirtyRecovery) ? null : draft;

  const persistSnapshotForScope = useCallback(
    (
      value: DraftToSave,
      protectDirty: boolean,
      requestStorageScope: string | null,
      requestViewScope: string,
      options?: DraftSaveOptions,
    ) => {
      const attempt = (localPersistenceAttempts.current.get(requestViewScope) ?? 0) + 1;
      localPersistenceAttempts.current.set(requestViewScope, attempt);
      if (!protectDirty && activeEditorScope.current === requestViewScope) {
        // A clean snapshot is requested only after the server acknowledged all
        // local work. Disk caching may still fail, but the earlier dirty-write
        // warning is no longer true once the authoritative save succeeded.
        localPersistenceFailed.current = false;
        localPersistenceFailureMessage.current = null;
      }

      // Capture the storage scope with the snapshot. SessionProvider installs a
      // new global scope in a layout effect, before an old editor's debounce can
      // flush; deriving it inside saveDraft at execution time would misfile the
      // old branch's quote under the new branch.
      const saving = options
        ? saveDraft(value, requestStorageScope ?? undefined, options)
        : saveDraft(value, requestStorageScope ?? undefined);
      void saving.then((result) => {
        if (
          activeEditorScope.current !== requestViewScope ||
          attempt !== localPersistenceAttempts.current.get(requestViewScope) ||
          !protectDirty
        ) {
          return;
        }
        const message = draftSaveFailureMessage(result);
        if (message) {
          localPersistenceFailed.current = true;
          localPersistenceFailureMessage.current = message;
          setSaveState('error');
          setSaveError(message);
          return;
        }

        if (!localPersistenceFailed.current) return;
        localPersistenceFailed.current = false;
        localPersistenceFailureMessage.current = null;
        setSaveError(null);
        setSaveState(
          editCounter.current === savedCounter.current ? 'saved' : onlineRef.current ? 'pending' : 'offline',
        );
      });
    },
    [],
  );

  const persistSnapshot = useCallback(
    (value: DraftToSave, protectDirty: boolean, options?: DraftSaveOptions) => {
      persistSnapshotForScope(value, protectDirty, quoteStorageScope, quoteViewScope, options);
    },
    [persistSnapshotForScope, quoteStorageScope, quoteViewScope],
  );

  const acknowledgmentFor = useCallback(
    (throughRevision: number): DraftSaveOptions => {
      const inherited = inheritedDraftAcknowledgment.current;
      const acknowledgments: DraftAcknowledgment[] = [{ editOwnerId, throughRevision }];
      if (inherited?.identity && inherited.identity.editOwnerId !== editOwnerId) {
        acknowledgments.push(inherited.identity);
      }
      return {
        acknowledgments,
        ...(inherited?.legacy ? { legacyAcknowledgment: inherited.legacy } : {}),
      };
    },
    [editOwnerId],
  );

  // Load the local copy first. A dirty snapshot has to appear immediately: it
  // is the only copy of keystrokes the server has not seen. A clean one is
  // the same while offline, which is the basement case this store exists for.
  // Online, a clean snapshot can still say Scheduled after the server has
  // recounted the series to zero, so it stays off screen until the live GET
  // answers. If that read never arrives, the snapshot is what we have.
  useEffect(() => {
    let canceled = false;
    const lookupScope = quoteViewScope;
    draftLookupReadyScopeRef.current = null;
    localPersistenceAttempts.current.set(
      quoteViewScope,
      (localPersistenceAttempts.current.get(quoteViewScope) ?? 0) + 1,
    );
    localPersistenceFailed.current = false;
    localPersistenceFailureMessage.current = null;
    setDraftLookupComplete(false);
    setLiveReadGraceElapsed(false);
    setStoredDraft(null);
    inheritedDraftAcknowledgment.current = null;
    setRestoreState('none');
    setDraft(null);
    draftRef.current = null;
    editCounter.current = 0;
    savedCounter.current = 0;
    setSaveState('idle');
    setSaveError(null);
    setLastSavedAt(null);
    setEditorStateScope(quoteViewScope);
    inFlight.current = null;
    resaveWhenDone.current = false;
    const abandonedWaiters = queuedWaiters.current;
    queuedWaiters.current = [];
    abandonedWaiters.forEach((resolve) => resolve(null));

    void (async () => {
      const stored = quoteStorageScope && mayReadQuote ? await loadDraft(quoteId, quoteStorageScope) : null;
      if (canceled) return;

      setStoredDraft(stored);
      if (stored) {
        // Online, wait for the live quote before painting a clean snapshot.
        // The first status on screen should be the one the server just counted.
        if (draftIsDirty(stored) || !onlineRef.current) {
          setDraft(stored.quote);
          draftRef.current = stored.quote;
        }

        if (draftIsDirty(stored)) {
          inheritedDraftAcknowledgment.current =
            stored.editOwnerId && Number.isSafeInteger(stored.editRevision)
              ? {
                  identity: {
                    editOwnerId: stored.editOwnerId,
                    throughRevision: stored.editRevision as number,
                  },
                  legacy: null,
                }
              : {
                  identity: null,
                  legacy: { baseVersion: stored.baseVersion, quote: stored.quote },
                };
          // One synthetic edit is enough to keep all of the existing dirty /
          // saved-counter machinery honest. The exact historical keystroke
          // count is irrelevant; only "server has not seen this" matters.
          editCounter.current = 1;
          savedCounter.current = 0;
          setRestoreState('waiting_for_server');
          setSaveState(onlineRef.current ? 'pending' : 'offline');
        } else {
          setSaveState('saved');
          setLastSavedAt(new Date(stored.lastSyncedAt ?? stored.updatedAt));
        }
      }
      draftLookupReadyScopeRef.current = lookupScope;
      setDraftLookupComplete(true);
    })();
    return () => {
      canceled = true;
    };
  }, [mayReadQuote, quoteId, quoteStorageScope, quoteViewScope, renderActivationGeneration]);

  useEffect(() => {
    if (!draftLookupComplete || !definitiveLoadError) return;
    if (hasDirtyRecovery) return;

    // A clean snapshot is merely a loading convenience. Once the server says
    // this quote is gone or forbidden, remove that convenience immediately so
    // customer data is not displayed behind a definitive live response.
    localPersistenceAttempts.current.set(
      quoteViewScope,
      (localPersistenceAttempts.current.get(quoteViewScope) ?? 0) + 1,
    );
    setStoredDraft(null);
    setDraft(null);
    draftRef.current = null;
    setSaveState('idle');
    setSaveError(null);
    void clearDraft(quoteId, quoteStorageScope ?? undefined);
  }, [definitiveLoadError, draftLookupComplete, hasDirtyRecovery, quoteId, quoteStorageScope, quoteViewScope]);

  // Reconcile after the local copy is already on screen. Dirty work is only
  // pushed when the server still has the version it was based on. A mismatch
  // remains a visible conflict with BOTH copies intact; clearing the IndexedDB
  // row here would turn conflict detection into data loss.
  useEffect(() => {
    if (!draftLookupComplete || draftLookupReadyScopeRef.current !== quoteViewScope) return;
    if (!adoptableServerQuote) return;

    if (restoreState === 'waiting_for_server' && storedDraft) {
      if (storedDraft.baseVersion === adoptableServerQuote.version) {
        setRestoreState('ready_to_sync');
        setSaveState('pending');
      } else {
        setRestoreState('conflict');
        setSaveState('conflict');
        setSaveError('This quote changed elsewhere while this device was offline. Your local copy is still safe.');
      }
      return;
    }

    if (restoreState !== 'none') return;
    if (editCounter.current !== savedCounter.current) return;

    // No unsaved work: the network copy is authoritative, and becomes the
    // next bounded clean snapshot for a cold offline open.
    setDraft(adoptableServerQuote);
    draftRef.current = adoptableServerQuote;
    const syncedAt = Date.now();
    persistSnapshot(
      {
        quoteId,
        baseVersion: adoptableServerQuote.version,
        quote: adoptableServerQuote,
        isDirty: false,
        lastSyncedAt: syncedAt,
      },
      false,
    );
  }, [adoptableServerQuote, draftLookupComplete, persistSnapshot, quoteId, quoteViewScope, restoreState, storedDraft]);

  // Online, a clean snapshot waits briefly for the live GET so a stale Scheduled
  // badge does not flash. Without a bound, a hung request (no client timeout)
  // would spin forever even though IndexedDB already has the quote.
  useEffect(() => {
    if (!draftLookupComplete || draftLookupReadyScopeRef.current !== quoteViewScope) return;
    if (!storedDraft || storedDraft.quoteId !== quoteId || draftIsDirty(storedDraft) || !isOnline) return;
    if (!query.isFetching && !query.isLoading) return;
    const timer = window.setTimeout(() => {
      setLiveReadGraceElapsed(true);
      const stored = storedDraftRef.current;
      if (
        stored &&
        stored.quoteId === quoteId &&
        draftLookupReadyScopeRef.current === quoteViewScope &&
        !draftIsDirty(stored) &&
        !draftRef.current &&
        onlineRef.current
      ) {
        setDraft(stored.quote);
        draftRef.current = stored.quote;
      }
    }, CLEAN_SNAPSHOT_LIVE_READ_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [draftLookupComplete, isOnline, query.isFetching, query.isLoading, quoteId, quoteViewScope, storedDraft]);

  // The clean snapshot stays off screen while a live read can still answer.
  // If that read fails without a definitive "gone" or "forbidden", show the
  // snapshot: it is the quote we have. This waits until the lookup that
  // belongs to the current quote has finished, so a quote change cannot
  // paint the previous snapshot onto the next one.
  useEffect(() => {
    if (!draftLookupComplete || draftLookupReadyScopeRef.current !== quoteViewScope) return;
    if (!storedDraft || storedDraft.quoteId !== quoteId || draftIsDirty(storedDraft)) return;
    if (definitiveLoadError || draftRef.current) return;
    if (isOnline && !liveReadGraceElapsed && (query.isFetching || query.isLoading)) return;
    if (isOnline && query.isSuccess && draftRef.current === null) return;
    setDraft(storedDraft.quote);
    draftRef.current = storedDraft.quote;
  }, [
    definitiveLoadError,
    draftLookupComplete,
    isOnline,
    liveReadGraceElapsed,
    query.isFetching,
    query.isLoading,
    query.isSuccess,
    quoteId,
    quoteViewScope,
    storedDraft,
  ]);

  const persistLocally = useDebouncedCallback(
    (
      quote: Quote,
      editRevision: number,
      requestQuoteId: string,
      requestStorageScope: string | null,
      requestViewScope: string,
      requestEditOwnerId: string,
    ) => {
      persistSnapshotForScope(
        {
          quoteId: requestQuoteId,
          baseVersion: quote.version,
          quote,
          isDirty: true,
          editOwnerId: requestEditOwnerId,
          editRevision,
          lastSyncedAt: null,
        },
        true,
        requestStorageScope,
        requestViewScope,
      );
    },
    500,
  );
  // `useDebouncedCallback` returns a small wrapper object each render while
  // these methods themselves are stable. Depend on the methods: depending on
  // the wrapper would run the leave-page cleanup after every keystroke,
  // defeating both debounces and starting saves during ordinary renders.
  const persistLocallyRun = persistLocally.run;
  const persistLocallyFlush = persistLocally.flush;
  const persistLocallyFlushRef = useRef(persistLocallyFlush);
  persistLocallyFlushRef.current = persistLocallyFlush;

  const save = useCallback(async (): Promise<Quote | null> => {
    const current = draftRef.current;
    if (!current) return null;
    if (!mayWriteQuote) return null;
    if (editCounter.current === savedCounter.current) return current;

    // A recovered draft must first compare its base version with a fresh GET.
    // This keeps reconnect from blindly overwriting work saved on another
    // device while this one was away.
    if (restoreState === 'waiting_for_server') return null;

    // A save is already going out and it does not carry the edits made since.
    // Mark that another is owed and hand back a promise that settles when *that*
    // one lands — returning the in-flight promise would resolve as soon as the
    // earlier request finished, and `withFreshVersion` would go on to publish,
    // send or render a version missing the latest edits.
    if (inFlight.current) {
      resaveWhenDone.current = true;
      return new Promise<Quote | null>((resolve) => {
        queuedWaiters.current.push(resolve);
      });
    }

    if (!isOnline) {
      if (!localPersistenceFailed.current) setSaveState('offline');
      return null;
    }

    const counterAtStart = editCounter.current;
    const requestViewScope = quoteViewScope;
    const requestActivationGeneration = renderActivationGeneration;
    const requestAcknowledgments = acknowledgmentFor(counterAtStart);
    const protectionFailure = localPersistenceFailureMessage.current;
    // A network attempt is useful, but it does not make an unprotected draft
    // safe. Keep the blocking storage warning visible until either IndexedDB
    // accepts the work or the server acknowledges it.
    setSaveState(protectionFailure ? 'error' : 'saving');
    setSaveError(protectionFailure);

    const request = (async () => {
      try {
        const server = await quoteApi.update(quoteId, current.version, buildChanges(current));
        const requestScopeChanged = activeEditorScope.current !== requestViewScope;
        const requestActivationChanged =
          currentEditorActivationGeneration(requestViewScope) !== requestActivationGeneration;
        if (requestScopeChanged || requestActivationChanged) {
          // A different editor activation may have created another dirty row
          // after this callback last observed memory. Any ACK-derived write
          // carries a higher server version and could therefore overwrite that
          // newer lower-version row in either IndexedDB transaction order.
          // Leave recovery untouched; reopening may surface a conservative
          // version conflict, but never loses the later edit.
          return server;
        }
        savedCounter.current = counterAtStart;
        // The recovered row has now been acknowledged. Keeping its original
        // dirty object in memory would make a later 403/404 look like unsaved
        // work still needed recovery, even after the clean snapshot below was
        // written successfully.
        setStoredDraft(null);

        const stillEditing = editCounter.current !== counterAtStart;
        const next = stillEditing ? mergeServerIds(draftRef.current ?? current, current, server) : server;
        setDraft(next);
        draftRef.current = next;
        queryClient.setQueryData(quoteQueryKey, server);
        setLastSavedAt(new Date());
        setSaveState(stillEditing ? 'pending' : 'saved');
        setRestoreState('none');

        if (stillEditing) {
          // The local copy is still ahead of the server, so the recovery draft
          // has to stay — and be refreshed with the ids the server just issued.
          persistSnapshot(
            {
              quoteId,
              baseVersion: next.version,
              quote: next,
              isDirty: true,
              editOwnerId,
              editRevision: editCounter.current,
              lastSyncedAt: Date.now(),
            },
            true,
            requestAcknowledgments,
          );
        } else {
          // Keep a clean, bounded snapshot. It is what makes a quote that was
          // merely viewed (or just successfully saved) open with no network.
          persistSnapshot(
            {
              quoteId,
              baseVersion: server.version,
              quote: server,
              isDirty: false,
              lastSyncedAt: Date.now(),
            },
            false,
            requestAcknowledgments,
          );
        }
        return server;
      } catch (error) {
        if (
          activeEditorScope.current !== requestViewScope ||
          currentEditorActivationGeneration(requestViewScope) !== requestActivationGeneration
        ) {
          return null;
        }
        if (error instanceof ApiRequestError && error.isVersionConflict) {
          setRestoreState('conflict');
          setSaveState('conflict');
          setSaveError(
            localPersistenceFailureMessage.current
              ? `${error.message} ${localPersistenceFailureMessage.current}`
              : error.message,
          );
          return null;
        }
        setSaveState('error');
        const requestMessage = error instanceof Error ? error.message : 'Could not save your changes.';
        setSaveError(
          localPersistenceFailureMessage.current
            ? `${localPersistenceFailureMessage.current} The online save also failed: ${requestMessage}`
            : requestMessage,
        );
        return null;
      }
    })();

    // Register this before the queued-save continuation below. Promise
    // reactions run in registration order, so a follow-up save must see that
    // the request it follows is no longer in flight.
    inFlight.current = request;
    const clearInFlight = () => {
      if (inFlight.current === request) inFlight.current = null;
    };
    void request.then(clearInFlight, clearInFlight);

    // Once this request settles, send anything that piled up behind it — but
    // only if it actually succeeded. On a conflict, validation error or 403 the
    // edit counter stays ahead forever, so retrying here would spin in a
    // delay-free loop and keep overwriting the error the user needs to see.
    // A failed save waits for the next edit or an explicit retry.
    void request.then(async (server) => {
      if (
        activeEditorScope.current !== requestViewScope ||
        currentEditorActivationGeneration(requestViewScope) !== requestActivationGeneration
      ) {
        return;
      }
      const queued = resaveWhenDone.current;
      resaveWhenDone.current = false;

      // Snapshotted before the follow-up runs: callers that queued behind *this*
      // request are waiting on the follow-up, while anything queued during the
      // follow-up belongs to its own continuation.
      const waiting = queuedWaiters.current;
      queuedWaiters.current = [];
      const settle = (result: Quote | null) => waiting.forEach((resolve) => resolve(result));

      if (server === null) return settle(null);
      if (!queued && editCounter.current === savedCounter.current) return settle(server);

      settle((await saveRef.current?.()) ?? null);
    });

    return request;
  }, [
    acknowledgmentFor,
    editOwnerId,
    isOnline,
    mayWriteQuote,
    persistSnapshot,
    queryClient,
    quoteId,
    quoteQueryKey,
    quoteStorageScope,
    quoteViewScope,
    renderActivationGeneration,
    restoreState,
  ]);

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  // Keep the latest committed save callback for each mounted route id. During
  // a route-param change React has already rendered the incoming quote before
  // it cleans up the outgoing effect; a single "latest" ref can therefore
  // pair the old draftRef with the new quote id. The per-id entry lets cleanup
  // address the quote that actually owned those edits, while an ordinary
  // location/auth rerender updates that quote's entry without causing a save.
  const leaveSaveByQuoteId = useRef(new Map<string, () => Promise<Quote | null>>());
  useLayoutEffect(() => {
    leaveSaveByQuoteId.current.set(quoteId, save);
  }, [quoteId, save]);

  const autosave = useDebouncedCallback(() => {
    void save();
  }, AUTOSAVE_DELAY_MS);
  const autosaveRun = autosave.run;
  const autosaveCancel = autosave.cancel;

  // A count, not a flag: two overlapping actions (send while a PDF is still
  // rendering) each suspend, and the first to finish must not un-suspend the
  // one still in flight.
  const autosaveSuspensions = useRef(0);
  const suspendAutosave = useCallback(() => {
    autosaveSuspensions.current += 1;
    autosaveCancel();
    let resumed = false;
    return () => {
      // A resume called twice must not hand back someone else's suspension.
      if (resumed) return;
      resumed = true;
      autosaveSuspensions.current -= 1;
      if (autosaveSuspensions.current === 0 && editCounter.current !== savedCounter.current) autosaveRun();
    };
  }, [autosaveCancel, autosaveRun]);

  const update = useCallback(
    (mutate: (current: Quote) => Quote) => {
      if (!mayWriteQuote) return;
      setDraft((current) => {
        if (!current) return current;
        // A square-foot price replaces room prices, so every edit to an area
        // quote (picking the mode, adding a room type with a price) drops them.
        const next = withoutAreaRoomPrices(mutate(current));
        draftRef.current = next;
        editCounter.current += 1;
        persistLocallyRun(next, editCounter.current, quoteId, quoteStorageScope, quoteViewScope, editOwnerId);
        // Edits made while an action holds the autosave are pushed when it
        // resumes — arming here would race the action's version bump.
        if (autosaveSuspensions.current === 0) autosaveRun();
        return next;
      });
      setSaveState((state) => (state === 'conflict' || localPersistenceFailed.current ? state : 'pending'));
    },
    [autosaveRun, editOwnerId, mayWriteQuote, persistLocallyRun, quoteId, quoteStorageScope, quoteViewScope],
  );

  const reloadFromServer = useCallback(async () => {
    autosaveCancel();
    const requestViewScope = quoteViewScope;
    const requestActivationGeneration = renderActivationGeneration;
    const requestEditCounter = editCounter.current;
    // Conflict resolution is destructive: this is the copy that replaces the
    // user's protected local draft. Go straight to the network-only endpoint
    // instead of `fetchQuery`: TanStack may return a still-fresh memory value
    // (or deduplicate against an ordinary cacheable GET) without invoking the
    // supplied query function at all.
    const fresh = await quoteApi.getFresh(quoteId);
    if (
      activeEditorScope.current !== requestViewScope ||
      currentEditorActivationGeneration(requestViewScope) !== requestActivationGeneration
    ) {
      // Scope cleanup may already have persisted an edit made after the user
      // clicked Reload latest. We can no longer compare this request's edit
      // counter with the old editor's reset refs, so do not let a late clean
      // snapshot overwrite that newer old-scope recovery row.
      return;
    }
    // Reload latest discards only the draft that existed when the user made
    // that choice. The editor remains usable while the network-only request is
    // traveling, so a later keystroke is newer work and must win rather than
    // being silently replaced by the response.
    if (editCounter.current !== requestEditCounter) {
      setSaveState('conflict');
      setSaveError('The quote changed again while loading the server copy. The newer local edits were kept.');
      return;
    }
    queryClient.setQueryData(quoteQueryKey, fresh);
    // Only discard the local side after the requested server copy actually
    // arrived. Clicking reload during an outage must not erase the draft.
    savedCounter.current = editCounter.current;
    setStoredDraft(null);
    setDraft(fresh);
    draftRef.current = fresh;
    setRestoreState('none');
    setSaveState('idle');
    setSaveError(null);
    const syncedAt = Date.now();
    persistSnapshot(
      {
        quoteId,
        baseVersion: fresh.version,
        quote: fresh,
        isDirty: false,
        lastSyncedAt: syncedAt,
      },
      false,
      acknowledgmentFor(requestEditCounter),
    );
  }, [
    acknowledgmentFor,
    autosaveCancel,
    persistSnapshot,
    queryClient,
    quoteId,
    quoteQueryKey,
    quoteStorageScope,
    quoteViewScope,
    renderActivationGeneration,
  ]);

  /**
   * Adopts the quote returned by an action (publish, send, PDF, a status
   * change) that ran on top of an autosave.
   *
   * `savedThrough` is the edit count taken when the action started. Anything
   * typed after that is not in the response — a PDF render in particular can
   * take many seconds — so marking the editor clean at the *current* count
   * would drop those edits and stop the pending autosave from noticing. Only
   * the work the server actually saw is marked saved; the rest is grafted back
   * on and pushed.
   *
   * No id mapping happens here. The action's prerequisite save already merged
   * the ids it issued, and rows added since are rows the server has never seen
   * — positional alignment against this response would hand them the wrong id.
   */
  const applyServerQuote = useCallback(
    (quote: Quote, savedThrough?: number) => {
      const actionScopeChanged = activeEditorScope.current !== quoteViewScope;
      const actionActivationChanged = currentEditorActivationGeneration(quoteViewScope) !== renderActivationGeneration;
      if (actionScopeChanged || actionActivationChanged) {
        // As with a PATCH ACK, a response owned by an older activation cannot
        // safely write a higher-version snapshot over this scope. The current
        // activation's dirty recovery row is the only non-destructive answer.
        return;
      }
      const counter = savedThrough ?? editCounter.current;
      savedCounter.current = counter;
      setStoredDraft(null);

      const local = draftRef.current;
      const stillEditing = editCounter.current !== counter;
      const next = stillEditing && local ? graftLocalEdits(local, quote) : quote;
      setDraft(next);
      draftRef.current = next;
      queryClient.setQueryData(quoteQueryKey, quote);
      setLastSavedAt(new Date());
      setSaveState(stillEditing ? 'pending' : 'saved');
      setRestoreState('none');

      if (stillEditing) {
        persistSnapshot(
          {
            quoteId,
            baseVersion: next.version,
            quote: next,
            isDirty: true,
            editOwnerId,
            editRevision: editCounter.current,
            lastSyncedAt: Date.now(),
          },
          true,
          acknowledgmentFor(counter),
        );
        void saveRef.current?.();
      } else {
        persistSnapshot(
          {
            quoteId,
            baseVersion: quote.version,
            quote,
            isDirty: false,
            lastSyncedAt: Date.now(),
          },
          false,
          acknowledgmentFor(counter),
        );
      }
    },
    [
      acknowledgmentFor,
      editOwnerId,
      persistSnapshot,
      queryClient,
      quoteId,
      quoteQueryKey,
      quoteStorageScope,
      quoteViewScope,
      renderActivationGeneration,
    ],
  );

  // Best-effort flush when the tab is hidden or closed.
  useEffect(() => {
    const flush = () => {
      if (editCounter.current !== savedCounter.current) void save();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', flush);
    };
  }, [save]);

  // SessionProvider changes its global draft scope in a layout effect. Flush a
  // pending local debounce with this render's explicit scope before the editor
  // resets for another location or read-access boundary. Network autosave is
  // intentionally left to the old in-flight request; after a location switch,
  // only the durable old-scope copy is universally safe to write.
  useEffect(
    () => () => {
      persistLocallyFlushRef.current();
    },
    [quoteStorageScope],
  );

  // Leaving via a link is client-side navigation: no `beforeunload` fires, and
  // unmounting cancels the debounced autosave and draft write. Both are flushed
  // here so an edit made in the last two seconds is not silently discarded.
  useLayoutEffect(() => {
    const leavingQuoteId = quoteId;
    return () => {
      persistLocallyFlushRef.current();
      const leavingSave = leaveSaveByQuoteId.current.get(leavingQuoteId);
      leaveSaveByQuoteId.current.delete(leavingQuoteId);
      if (editCounter.current !== savedCounter.current) void leavingSave?.();
    };
  }, [quoteId]);

  // Reconnecting should push whatever accumulated while offline (spec §17.2).
  useEffect(() => {
    if (!isOnline) {
      // A request can fail just before the browser emits its offline event. In
      // that ordering the catch above has already selected `error`; normalize
      // it here so the next online event knows there is work to replay.
      if (editCounter.current !== savedCounter.current && saveState !== 'conflict' && !localPersistenceFailed.current) {
        setSaveState('offline');
      }
      return;
    }
    if (restoreState === 'ready_to_sync') {
      if (!mayWriteQuote) return;
      setRestoreState('none');
      void save();
      return;
    }
    if (restoreState === 'waiting_for_server' || restoreState === 'conflict') return;
    if (localPersistenceFailed.current && editCounter.current !== savedCounter.current) {
      void save();
      return;
    }
    if (saveState === 'offline') void save();
  }, [isOnline, mayWriteQuote, restoreState, save, saveState]);

  const preview = useMemo<QuoteCalculationResult>(
    () => (visibleDraft ? calculateQuote(calculationInputFor(visibleDraft)) : EMPTY_PREVIEW),
    [visibleDraft],
  );

  return {
    quote: visibleDraft,
    serverQuote,
    // IndexedDB is part of loading now. A dirty snapshot, or a clean one while
    // offline, is the quote, so a paused network query must not hold the
    // editor behind a spinner. Online, a clean snapshot is withheld until the
    // live GET is copied into the draft: dropping the spinner a frame early
    // reads as "this quote may have been deleted".
    isLoading:
      visibleDraft === null &&
      !definitiveLoadError &&
      (!draftLookupComplete ||
        query.isLoading ||
        (isOnline &&
          !hasDirtyRecovery &&
          !liveReadGraceElapsed &&
          (query.isFetching || (query.isSuccess && draft === null)))),
    loadError:
      visibleDraft && (!definitiveLoadError || hasDirtyRecovery) ? null : ((query.error as Error | null) ?? null),
    saveState,
    saveError,
    lastSavedAt,
    isDirty,
    isOnline,
    preview,
    update,
    save,
    reloadFromServer,
    applyServerQuote,
    savedEditCount: () => savedCounter.current,
    suspendAutosave,
  };
}
